import { useEffect, useRef, useState } from "react";
import { IconCheck, IconChevronDown } from "@tabler/icons-react";
import { useText } from "../../clients/words";
import { Glif } from "./ZnachokKanala";
import { CVETA_MESSENDZHEROV } from "../cvetaMessendzherov";

export interface Liniya {
  id: number;
  kind: string;
  slovo: string;
  filial_id?: number | null;
  filial_slovo?: string;
  set?: boolean;
}

/**
 * ⛔⛔ ФИЛЬТР «ЛИНИЯ» — ВТОРОЙ ПОСЛЕ ФИЛИАЛА ШАПКИ (владелец, 24.09.2026,
 * `[s635-filtr-linii]`: «второй фильтр кроме филиала - по мессенджеру, по номеру,
 * чтобы я могу вывести напрмиер толко 7925505 или толко 7929633 или только тг
 * определенный»). Список и слова — с сервера (справочник каналов), выбор одной
 * или нескольких линий; считает сервер. Один компонент на очередь и доску лидов.
 */
export function VyborLiniy({
  linii,
  vybrany,
  onVybor,
  className,
  messenger = false,
}: {
  linii: Liniya[];
  vybrany: number[];
  onVybor: (ids: number[]) => void;
  className?: string;
  messenger?: boolean;
}) {
  const text = useText();
  const [otkryt, setOtkryt] = useState(false);
  const [poisk, setPoisk] = useState('');
  const korob = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!otkryt) return;
    const zakryt = (e: MouseEvent) => {
      if (korob.current !== null && !korob.current.contains(e.target as Node)) setOtkryt(false);
    };
    document.addEventListener("mousedown", zakryt);

    return () => document.removeEventListener("mousedown", zakryt);
  }, [otkryt]);

  if (linii.length === 0 && !messenger) return null;

  const vse = text("obrashcheniya.linii.vse");
  const podpis = vybrany.length === 0
    ? vse
    : vybrany.length === 1
      ? (linii.find((l) => l.id === vybrany[0])?.slovo ?? vse)
      : text("obrashcheniya.linii.neskolko", { число: vybrany.length });

  const pereklyuchit = (id: number) =>
    onVybor(vybrany.includes(id) ? vybrany.filter((v) => v !== id) : [...vybrany, id].sort((a, b) => a - b));

  const visible = linii.filter(l => [l.slovo,l.filial_slovo].filter(Boolean).join(' ').toLowerCase().includes(poisk.toLowerCase()));
  const grouped = messenger ? [...visible.filter(l => l.set ?? l.filial_id === null), ...visible.filter(l => !(l.set ?? l.filial_id === null))] : visible;
  return (
    <div ref={korob} className={`relative ${messenger ? 'messenger-lines' : ''} ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => setOtkryt((b) => !b)}
        aria-haspopup="listbox"
        aria-expanded={otkryt}
        data-vybor-linii
        className={
          "flex h-full max-w-[260px] items-center gap-1 rounded-ops-pill border px-3 py-1 text-ops-caption "
          + (vybrany.length > 0
            ? "border-ops-primary bg-ops-primary text-ops-on-primary"
            : "border-ops-border text-ops-text-secondary hover:bg-ops-surface-2")
        }
      >
        <span className="truncate">{podpis}</span>
        <IconChevronDown size={14} className="shrink-0" />
      </button>

      {otkryt && (
        <div
          role="listbox"
          aria-multiselectable
          className={`absolute left-0 top-full z-40 mt-1 min-w-[250px] rounded-ops-card border border-ops-border bg-ops-surface shadow-ops-drop ${messenger ? 'messenger-line-menu' : 'overflow-hidden'}`}
        >
          {messenger && <input type="search" value={poisk} onChange={e => setPoisk(e.target.value)} placeholder={text('messenger.lines.search')} aria-label={text('messenger.lines.search')} />}
          <button
            type="button"
            onClick={() => {
              onVybor([]);
              setOtkryt(false);
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-ops-caption text-ops-text hover:bg-ops-surface-2"
          >
            <span className="flex-1">{vse}</span>
            {vybrany.length === 0 && <IconCheck size={15} className="shrink-0 text-ops-primary" />}
          </button>
          {grouped.map((l, i) => (
            <div key={l.id}>
            {messenger && (i === 0 || Boolean(grouped[i-1].set ?? grouped[i-1].filial_id === null) !== Boolean(l.set ?? l.filial_id === null)) && <div className="line-group">{text((l.set ?? l.filial_id === null) ? 'messenger.lines.network' : 'messenger.lines.branch')}</div>}
            <button
              key={l.id}
              type="button"
              role="option"
              aria-selected={vybrany.includes(l.id)}
              onClick={() => pereklyuchit(l.id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-ops-caption text-ops-text hover:bg-ops-surface-2"
            >
              <span className="shrink-0" style={{ color: CVETA_MESSENDZHEROV[l.kind] ?? "#64748B" }}>
                <Glif kind={l.kind} razmer={16} />
              </span>
              <span className={`flex-1 ${messenger ? 'line-label' : 'truncate'}`}>{l.slovo}{messenger && !(l.set ?? l.filial_id === null) && l.filial_slovo && <span className="block text-ops-text-secondary">{l.filial_slovo}</span>}</span>
              {vybrany.includes(l.id) && <IconCheck size={15} className="shrink-0 text-ops-primary" />}
            </button>
            </div>
          ))}
          {messenger && grouped.length === 0 && <div className="line-group">{text('messenger.lines.empty')}</div>}
        </div>
      )}
    </div>
  );
}
